import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productService, pageService } from '../services/api';
import AdSlot from './AdSlot';
import { updateMetaTags } from '../utils/seo';

const brandData = {
  food: {
    title: 'Food & Beverages',
    subtitle: 'Delicious brands that bring taste to every home.',
    metaTitle: 'Food & Beverages | Ortus FMCG',
    metaDesc: "Explore Ortus FMCG's food and beverage portfolio — Golden Harvest, Fresh & Pure, and more. Premium quality food products crafted for modern India.",
    gradient: 'from-orange-500 to-red-500',
    bgGradient: 'from-orange-900 via-red-900 to-slate-900',
    icon: '🍽',
    brands: [
      { name: 'Golden Harvest', desc: 'Premium staples, grains, and pulses sourced directly from farmers.', icon: '🌾' },
      { name: 'Fresh & Pure', desc: 'Cold-pressed oils, fresh dairy, and natural food products.', icon: '🥛' },
      { name: 'Desi Atta', desc: 'Traditional stone-ground flour for authentic Indian rotis.', icon: '🍞' },
      { name: 'Ortus Foods', desc: 'Ready-to-eat meals and snacks for the modern lifestyle.', icon: '🥘' },
    ],
    features: [
      { icon: '🌱', title: 'Farm Direct', desc: 'Sourced directly from 500+ partner farmers with fair pricing.' },
      { icon: '🏭', title: 'Zero Preservatives', desc: 'Natural processing with no artificial preservatives or additives.' },
      { icon: '📦', title: 'Fresh Packaging', desc: 'Advanced packaging technology locks in freshness and nutrition.' },
      { icon: '🏆', title: 'Quality Certified', desc: 'FSSAI certified with rigorous quality control at every stage.' },
    ],
    stats: [
      { value: '500+', label: 'Partner Farmers' },
      { value: '50+', label: 'SKUs' },
      { value: '20+', label: 'Cities' },
      { value: '100%', label: 'Natural' },
    ],
    content: '<p class="text-slate-600 leading-relaxed mb-6">Our Food & Beverages portfolio is built on a simple promise: to bring the freshest, highest-quality food products to every Indian household. From farm-sourced staples to ready-to-eat convenience foods, every product is crafted with care, quality, and nutrition at its core.</p><p class="text-slate-600 leading-relaxed mb-6">We work directly with farmers across India, ensuring fair prices for producers and fresh, natural products for consumers. Our state-of-the-art processing facilities maintain the highest standards of hygiene and quality, while our cold-chain logistics ensure that what reaches your home is as fresh as the day it was harvested.</p><p class="text-slate-600 leading-relaxed">From Golden Harvest grains to Fresh & Pure dairy, our brands are trusted by millions of families across India for their everyday food needs.</p>',
  },
  'home-care': {
    title: 'Home Care',
    subtitle: 'Keeping homes clean and fresh every day.',
    metaTitle: 'Home Care | Ortus FMCG',
    metaDesc: "Discover Ortus FMCG's home care brands — Ortus Home, CleanMate, CareMate, and Voom. Innovative cleaning solutions for modern Indian homes.",
    gradient: 'from-blue-500 to-cyan-500',
    bgGradient: 'from-blue-900 via-cyan-900 to-slate-900',
    icon: '🏠',
    brands: [
      { name: 'Ortus Home', desc: 'Complete home cleaning solutions for every surface.', icon: '🧽' },
      { name: 'CleanMate', desc: 'Powerful surface cleaners and disinfectants.', icon: '🧴' },
      { name: 'CareMate', desc: 'Fabric care and laundry detergents that protect clothes.', icon: '👕' },
      { name: 'Voom', desc: 'Air fresheners and home fragrance products.', icon: '🌸' },
    ],
    features: [
      { icon: '🛡', title: 'Germ Protection', desc: 'Kills 99.9% of germs and bacteria for a healthier home.' },
      { icon: '🌿', title: 'Eco-Friendly', desc: 'Biodegradable formulas that are safe for your family and the planet.' },
      { icon: '⚡', title: 'Powerful Action', desc: 'Advanced formulations that clean effectively with less effort.' },
      { icon: '🌸', title: 'Long-Lasting', desc: 'Refreshing fragrances that keep your home smelling great for hours.' },
    ],
    stats: [
      { value: '30+', label: 'Products' },
      { value: '99.9%', label: 'Germ Kill' },
      { value: '15+', label: 'Cities' },
      { value: '100%', label: 'Safe Formula' },
    ],
    content: '<p class="text-slate-600 leading-relaxed mb-6">A clean home is a happy home. Our Home Care portfolio delivers powerful, effective, and safe cleaning solutions that make maintaining a spotless home effortless. From surface cleaners to fabric care, every product is formulated with advanced technology and skin-safe ingredients.</p><p class="text-slate-600 leading-relaxed mb-6">We understand that every Indian home has unique cleaning needs — from kitchen grease to bathroom grime, from delicate fabrics to tough stains. That\'s why our brands offer specialized solutions for every cleaning challenge.</p><p class="text-slate-600 leading-relaxed">Our commitment to sustainability means our products are biodegradable, our packaging is recyclable, and our formulas are safe for your family and the environment.</p>',
  },
  'personal-care': {
    title: 'Personal Care',
    subtitle: 'Care that goes beyond the surface.',
    metaTitle: 'Personal Care | Ortus FMCG',
    metaDesc: "Explore Ortus FMCG's personal care brands — Ortus Care, Think Skin, Prim, and Pratha. Daily wellness and hygiene products for the whole family.",
    gradient: 'from-teal-500 to-emerald-500',
    bgGradient: 'from-teal-900 via-emerald-900 to-slate-900',
    icon: '🧴',
    brands: [
      { name: 'Ortus Care', desc: 'Daily hygiene essentials — soaps, hand washes, and sanitizers.', icon: '🧼' },
      { name: 'Think Skin', desc: 'Skincare solutions for healthy, glowing skin.', icon: '✨' },
      { name: 'Prim', desc: 'Hair care products for all hair types and concerns.', icon: '💇' },
      { name: 'Pratha', desc: 'Traditional Ayurvedic personal care with modern science.', icon: '🌿' },
    ],
    features: [
      { icon: '🌿', title: 'Natural Ingredients', desc: 'Enriched with natural extracts and Ayurvedic herbs.' },
      { icon: '🔬', title: 'Dermatologically Tested', desc: 'Safe for all skin types, tested by dermatologists.' },
      { icon: '💧', title: 'Deep Hydration', desc: 'Long-lasting moisture and nourishment for skin and hair.' },
      { icon: '👨\u200d👩\u200d👧', title: 'Family Safe', desc: 'Gentle formulas suitable for adults and children alike.' },
    ],
    stats: [
      { value: '25+', label: 'Products' },
      { value: '10+', label: 'Natural Herbs' },
      { value: '20+', label: 'Cities' },
      { value: '100%', label: 'Cruelty-Free' },
    ],
    content: '<p class="text-slate-600 leading-relaxed mb-6">Personal care is about more than just looking good — it\'s about feeling good, staying healthy, and caring for your body every day. Our Personal Care portfolio combines the wisdom of traditional Ayurveda with modern scientific research to create products that truly work.</p><p class="text-slate-600 leading-relaxed mb-6">From daily hygiene essentials to specialized skincare solutions, our brands cater to the diverse needs of Indian consumers. We use natural ingredients, avoid harsh chemicals, and ensure every product is dermatologically tested and safe for the whole family.</p><p class="text-slate-600 leading-relaxed">Our Pratha line brings the time-tested goodness of Ayurveda to modern personal care, while Think Skin offers science-backed skincare for the modern consumer. Whatever your personal care need, we have a product designed for you.</p>',
  },
  beauty: {
    title: 'Opulent Edge',
    subtitle: 'Beauty that empowers confidence.',
    metaTitle: 'Opulent Edge | Ortus FMCG',
    metaDesc: "Discover Ortus FMCG's beauty brands — Ortus Beauty, Kara, TS, and Mysst. Premium cosmetics and beauty products for the bold and beautiful.",
    gradient: 'from-purple-500 to-pink-500',
    bgGradient: 'from-purple-900 via-pink-900 to-slate-900',
    icon: '✨',
    brands: [
      { name: 'Ortus Beauty', desc: 'Premium cosmetics for the modern, confident woman.', icon: '💄' },
      { name: 'Kara', desc: 'Skincare-infused beauty products with natural ingredients.', icon: '🌸' },
      { name: 'TS', desc: 'Trend-setting color cosmetics for bold expressions.', icon: '🎨' },
      { name: 'Mysst', desc: 'Luxury fragrances and body mists for everyday elegance.', icon: '🫧' },
    ],
    features: [
      { icon: '💎', title: 'Premium Quality', desc: 'International-grade formulations with luxury ingredients.' },
      { icon: '🎨', title: 'Vibrant Colors', desc: 'Rich, long-lasting pigments for every skin tone.' },
      { icon: '🌸', title: 'Skin-Friendly', desc: 'Non-comedogenic, hypoallergenic, and enriched with skincare.' },
      { icon: '🐰', title: 'Cruelty-Free', desc: 'Never tested on animals. Beauty with a conscience.' },
    ],
    stats: [
      { value: '40+', label: 'Products' },
      { value: '50+', label: 'Shades' },
      { value: '15+', label: 'Cities' },
      { value: '100%', label: 'Cruelty-Free' },
    ],
    content: '<p class="text-slate-600 leading-relaxed mb-6">Opulent Edge is our premium beauty portfolio — a collection of brands designed for the bold, the beautiful, and the confident. We believe beauty is about self-expression, and our products give you the tools to express yourself fearlessly.</p><p class="text-slate-600 leading-relaxed mb-6">From high-pigment color cosmetics to skincare-infused beauty products, every item in our portfolio is crafted with premium ingredients and international-quality formulations. We combine the latest beauty trends with timeless elegance to create products that are both modern and enduring.</p><p class="text-slate-600 leading-relaxed">Our commitment to cruelty-free beauty means you can look gorgeous with a clear conscience. Every product is dermatologically tested, skin-friendly, and designed to enhance your natural beauty — not mask it.</p>',
  },
  'jv-brands': {
    title: 'JV Brands',
    subtitle: 'Strategic partnerships for global reach.',
    metaTitle: 'JV Brands | Ortus FMCG',
    metaDesc: "Explore Ortus FMCG's joint venture brands — Terra, Swiss Tempelle, Sensible Portions, and Dreamery. International expertise meets Indian markets.",
    gradient: 'from-indigo-500 to-blue-500',
    bgGradient: 'from-indigo-900 via-blue-900 to-slate-900',
    icon: '🤝',
    brands: [
      { name: 'Terra', desc: 'Organic and natural food products from sustainable farms.', icon: '🌍' },
      { name: 'Swiss Tempelle', desc: 'Premium personal care with Swiss formulation expertise.', icon: '🏔' },
      { name: 'Sensible Portions', desc: 'Healthy snacks for mindful eating.', icon: '🥗' },
      { name: 'Dreamery', desc: 'Luxury dairy desserts and ice creams.', icon: '🍦' },
    ],
    features: [
      { icon: '🌍', title: 'Global Standards', desc: 'International quality benchmarks and formulation expertise.' },
      { icon: '🤝', title: 'Strategic Partnerships', desc: 'Collaborations with global leaders in their respective categories.' },
      { icon: '🏭', title: 'Local Manufacturing', desc: 'Produced locally to ensure freshness and support Indian economy.' },
      { icon: '✅', title: 'Trusted Worldwide', desc: 'Brands trusted by consumers across multiple countries.' },
    ],
    stats: [
      { value: '4', label: 'JV Brands' },
      { value: '10+', label: 'Countries' },
      { value: '30+', label: 'Products' },
      { value: '100%', label: 'Global Quality' },
    ],
    content: '<p class="text-slate-600 leading-relaxed mb-6">Our Joint Venture portfolio brings together the best of global expertise and local insight. Through strategic partnerships with international brands, we introduce world-class products to the Indian market while maintaining the quality standards that Indian consumers deserve.</p><p class="text-slate-600 leading-relaxed mb-6">Each JV brand is chosen for its unique value proposition — whether it\'s Terra\'s commitment to organic farming, Swiss Tempelle\'s premium formulations, or Dreamery\'s indulgent desserts. We adapt these global brands to suit Indian tastes, preferences, and price points.</p><p class="text-slate-600 leading-relaxed">Our JV partnerships also enable knowledge transfer, technology sharing, and best practices that benefit our entire portfolio. It\'s a win-win for global brands entering India and for Indian consumers accessing world-class products.</p>',
  },
};

function BrandCategory() {
  const { slug } = useParams();
  const fallback = brandData[slug] || brandData.food;
  const [data, setData] = useState(fallback);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        const page = await pageService.show(slug);
        if (cancelled) return;
        const meta = page?.meta || page?.data?.meta || {};
        const merged = {
          ...fallback,
          ...(page?.title || page?.data?.title ? { title: page.title || page.data.title } : {}),
          ...(meta.subtitle ? { subtitle: meta.subtitle } : {}),
          ...(meta.gradient ? { gradient: meta.gradient } : {}),
          ...(meta.bgGradient ? { bgGradient: meta.bgGradient } : {}),
          ...(meta.icon ? { icon: meta.icon } : {}),
          ...(page?.content || page?.data?.content ? { content: page.content || page.data.content } : {}),
          ...(meta.brands ? { brands: meta.brands } : {}),
          ...(meta.features ? { features: meta.features } : {}),
          ...(meta.stats ? { stats: meta.stats } : {}),
          ...(page?.meta_title || page?.data?.meta_title ? { metaTitle: page.meta_title || page.data.meta_title } : {}),
          ...(page?.meta_description || page?.data?.meta_description ? { metaDesc: page.meta_description || page.data.meta_description } : {}),
        };
        setData(merged);
      } catch (e) {
        if (!cancelled) setData(fallback);
      }
      if (!cancelled) loadProducts();
    };
    loadData();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    updateMetaTags({
      title: data.metaTitle,
      description: data.metaDesc,
      siteName: 'Ortus FMCG'
    });
  }, [data]);

  const loadProducts = async () => {
    try {
      const result = await productService.index({ active_only: 1 });
      setProducts((result.data || []).slice(0, 8));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const cardColors = [
    { gradient: 'from-orange-500 to-red-500', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
    { gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
    { gradient: 'from-teal-500 to-emerald-500', bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
    { gradient: 'from-purple-500 to-pink-500', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className={`relative bg-gradient-to-br ${data.bgGradient} text-white py-24 md:py-32 overflow-hidden`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 blur-3xl rounded-full"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className={`h-1 w-20 bg-gradient-to-r ${data.gradient} rounded-full mb-6`}></div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-4xl">{data.icon}</span>
              <span className="text-red-400 font-bold uppercase tracking-[0.2em] text-xs">Ortus Portfolio</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tight">{data.title}</h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">{data.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop" className={`inline-flex items-center gap-2 bg-gradient-to-r ${data.gradient} text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105`}>
                Explore Products
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
              <Link to="/who-we-are" className="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-3.5 rounded-xl transition-all hover:scale-105">
                About Ortus
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Top Ad Slot */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        <AdSlot position="brand_top" className="h-[200px]" />
      </div>

      {/* Content Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-8">
            <div className={`h-1 w-16 bg-gradient-to-r ${data.gradient} rounded-full mb-5`}></div>
            <h2 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight">About Our {data.title}</h2>
          </div>
          <div className="prose prose-lg max-w-none text-slate-600 leading-relaxed text-lg [&_p]:mb-6 [&_p]:leading-relaxed" dangerouslySetInnerHTML={{ __html: data.content }} />
        </div>
      </section>

      {/* Brand Cards */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className={`h-1 w-16 bg-gradient-to-r ${data.gradient} rounded-full mx-auto mb-5`}></div>
            <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-3">Our Brands</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Discover the brands that make up our {data.title} portfolio.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.brands.map((brand, idx) => {
              const c = cardColors[idx % cardColors.length];
              return (
                <div
                  key={idx}
                  className={`group relative bg-white rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${c.border} overflow-hidden text-center`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c.gradient}`}></div>
                  <div className={`w-16 h-16 ${c.bg} ${c.text} rounded-2xl flex items-center justify-center mx-auto mb-5 text-3xl shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {brand.icon}
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 mb-2">{brand.name}</h3>
                  <div className={`w-10 h-0.5 bg-gradient-to-r ${c.gradient} mx-auto mb-3`}></div>
                  <p className="text-slate-500 text-sm leading-relaxed">{brand.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className={`h-1 w-16 bg-gradient-to-r ${data.gradient} rounded-full mx-auto mb-5`}></div>
            <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-3">Why Choose Our {data.title}</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Quality, innovation, and trust in every product.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.features.map((feature, idx) => {
              const c = cardColors[idx % cardColors.length];
              return (
                <div
                  key={idx}
                  className={`group bg-white rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${c.border} overflow-hidden`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c.gradient}`}></div>
                  <div className={`w-14 h-14 ${c.bg} ${c.text} rounded-2xl flex items-center justify-center mb-5 text-2xl shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-16 md:py-20 bg-gradient-to-br ${data.bgGradient} text-white relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 blur-3xl rounded-full"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <h2 className="text-2xl md:text-4xl font-black text-center mb-12">Our Impact</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.stats.map((stat, i) => (
              <div key={i} className="text-center group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 hover:-translate-y-1">
                  <div className={`text-3xl md:text-5xl font-black bg-gradient-to-r ${data.gradient} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300 inline-block`}>
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-slate-400 font-semibold uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      {products.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className={`h-1 w-16 bg-gradient-to-r ${data.gradient} rounded-full mx-auto mb-5`}></div>
              <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-3">Featured Products</h2>
              <p className="text-slate-500 max-w-2xl mx-auto">Explore some of our most popular products.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/shop/${product.slug || product.id}`}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-slate-100 overflow-hidden"
                >
                  <div className="h-48 bg-slate-100 flex items-center justify-center overflow-hidden">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="text-slate-300 text-sm">No image</div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-slate-900 group-hover:text-red-600 transition-colors">{product.name}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{product.description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-black text-red-700 text-lg">₹{product.discount_price || product.price}</span>
                      <span className={`text-xs font-bold bg-gradient-to-r ${data.gradient} text-white px-3 py-1 rounded-lg group-hover:scale-105 transition-transform`}>
                        View
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/shop"
                className={`inline-flex items-center gap-2 bg-gradient-to-r ${data.gradient} text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105`}
              >
                View All Products
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className={`h-1 w-16 bg-gradient-to-r ${data.gradient} rounded-full mx-auto mb-6`}></div>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight">Explore the Full Portfolio</h2>
          <p className="text-lg text-slate-400 leading-relaxed mb-8">Discover our complete range of brands across food, home care, personal care, and beauty — all designed for modern India.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/who-we-are" className={`inline-flex items-center gap-2 bg-gradient-to-r ${data.gradient} text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105`}>
              About Ortus
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-3.5 rounded-xl transition-all hover:scale-105">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Bottom Ad Slot */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <AdSlot position="brand_bottom" className="h-[250px]" />
      </div>
    </div>
  );
}

export default BrandCategory;
